// Modified from https://github.com/tanstack/table
// MIT License
// Changes from Qualcomm Technologies, Inc. are provided under the following license:
// Copyright (c) Qualcomm Technologies, Inc. and/or its subsidiaries.
// SPDX-License-Identifier: BSD-3-Clause-Clear

import type {AggregationFn} from "./features/grouping.js"
import {isNumberArray} from "./utils.js"

const sum: AggregationFn<any> = (columnId, _leafRows, childRows) => {
  // It's faster to just add the aggregations together instead of
  // process leaf nodes individually
  return childRows.reduce((sum, next) => {
    const nextValue = next.getValue(columnId)
    return sum + (typeof nextValue === "number" ? nextValue : 0)
  }, 0)
}

const min: AggregationFn<any> = (columnId, _leafRows, childRows) => {
  let min: number | undefined

  for (const row of childRows) {
    const value = row.getValue<number>(columnId)

    if (value != null && (min! > value || min === undefined)) {
      min = value
    }
  }

  return min
}

const max: AggregationFn<any> = (columnId, _leafRows, childRows) => {
  let max: number | undefined

  for (const row of childRows) {
    const value = row.getValue<number>(columnId)
    if (value != null && (max! < value || max === undefined)) {
      max = value
    }
  }

  return max
}

const extent: AggregationFn<any> = (columnId, _leafRows, childRows) => {
  let min: number | undefined
  let max: number | undefined

  for (const row of childRows) {
    const value = row.getValue<number>(columnId)
    if (value != null) {
      if (min === undefined) {
        min = max = value
      } else {
        if (min > value) {
          min = value
        }
        if (max! < value) {
          max = value
        }
      }
    }
  }

  return [min, max]
}

const mean: AggregationFn<any> = (columnId, leafRows) => {
  let count = 0
  let sum = 0

  for (const row of leafRows) {
    let value = row.getValue<number>(columnId)
    if (value != null && (value = +value) >= value) {
      ++count
      sum += value
    }
  }

  if (count) {
    return sum / count
  }

  return
}

const median: AggregationFn<any> = (columnId, leafRows) => {
  if (!leafRows.length) {
    return
  }

  const values = leafRows.map((row) => row.getValue(columnId))
  if (!isNumberArray(values)) {
    return
  }
  if (values.length === 1) {
    return values[0]
  }

  const mid = Math.floor(values.length / 2)
  const nums = values.sort((a, b) => a - b)
  return values.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2
}

const unique: AggregationFn<any> = (columnId, leafRows) => {
  return Array.from(new Set(leafRows.map((d) => d.getValue(columnId))).values())
}

const uniqueCount: AggregationFn<any> = (columnId, leafRows) => {
  return new Set(leafRows.map((d) => d.getValue(columnId))).size
}

const count: AggregationFn<any> = (_columnId, leafRows) => {
  return leafRows.length
}

export const aggregationFns: {
  count: AggregationFn<any>
  extent: AggregationFn<any>
  max: AggregationFn<any>
  mean: AggregationFn<any>
  median: AggregationFn<any>
  min: AggregationFn<any>
  sum: AggregationFn<any>
  unique: AggregationFn<any>
  uniqueCount: AggregationFn<any>
} = {
  count,
  extent,
  max,
  mean,
  median,
  min,
  sum,
  unique,
  uniqueCount,
}

export type BuiltInAggregationFn = keyof typeof aggregationFns
